import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouterState } from '@tanstack/react-router';
import { TopNavShell, BrandLockup } from '@noor/ui';
import { cn } from '@noor/lib/cn';
import { isSharedPath } from '@/entities/sub-app';
import { AppSwitcher } from '@/features/app-switcher';
import { MerchantChatCenter } from '@/features/merchant-chat';
import { NotificationsCenter } from '@/features/vendor-broadcasts';
import { AppVersionBadge } from '@/shared/ui/app-version-badge';
import { DELIVERY_NAV } from '../config/nav';
import { DeliveryAccountMenu } from './delivery-account-menu';

interface DeliveryShellLayoutProps {
  children: ReactNode;
}

/**
 * The Delivery (Доставка) sub-app chrome — the top nav bar with the noor logo +
 * app-switcher, the segmented route nav, and the theme/support/language/account
 * utilities. A twin of `EatsShellLayout` scoped to the `/delivery` namespace and
 * the `delivery` i18n resources; both compose the shared `TopNavShell`.
 */
export function DeliveryShellLayout({ children }: DeliveryShellLayoutProps) {
  const nav = DELIVERY_NAV;
  const { t } = useTranslation('delivery');
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isActive = (path: string) =>
    path === '/delivery' ? pathname === '/delivery' : pathname.startsWith(path);

  // PREFIX, not equality: `/profile` owns sub-routes (`/profile/users`, the
  // staff roster), and matching the exact path only left those rendering with
  // this sub-app's name and nav tabs — advertising a section the screen does
  // not belong to. `/support` is shared on the same terms.
  const isProfilePage = isSharedPath(pathname);

  return (
    <TopNavShell.Root className="h-dvh bg-muted">
      <TopNavShell.Header
        // The wordmark is the way back OUT of a sub-app: it returns to the
        // launcher rather than to this sub-app's own root, which the first nav
        // tab already covers.
        brand={
          <Link
            to="/welcome"
            aria-label={isProfilePage ? 'Noor' : 'Noor delivery'}
            className="rounded-md transition-transform duration-200 ease-out outline-none hover:scale-[1.03] focus-visible:ring-[3px] focus-visible:ring-ring/40 active:scale-95"
          >
            {/* No suffix on a shared route: `/profile` and `/support` belong to
                no sub-app, so naming one here tells the merchant they are
                somewhere they are not. The bare logo is the lockup's own
                documented form for exactly this case. */}
            <span className="flex items-end gap-2">
              {isProfilePage ? <BrandLockup /> : <BrandLockup suffix="delivery" />}
              <AppVersionBadge />
            </span>
          </Link>
        }
        // `/profile` is a shared route with no place in this sub-app's tab bar:
        // no tab owns it, so the whole row would render with nothing active.
        // Omitting `nav` entirely makes the header drop its nav row rather than
        // render an empty one.
        nav={
          isProfilePage ? undefined : (
            <TopNavShell.Nav className="hidden lg:flex">
              {nav.map((item) => (
                <TopNavShell.NavItem key={item.path} asChild active={isActive(item.path)}>
                  <Link to={item.path}>{t(item.labelKey)}</Link>
                </TopNavShell.NavItem>
              ))}
            </TopNavShell.Nav>
          )
        }
        utilities={
          <>
            <div
              className={cn(
                'items-center gap-2',
                !isProfilePage ? 'hidden lg:flex' : 'flex',
              )}
            >
              <AppSwitcher />
              {/* Support button hidden for now
              <IconButton
                asChild
                variant={isSupportPage ? 'default' : 'secondary'}
                size="default"
                className={cn(isProfilePage && 'hidden lg:flex', 'size-14')}
                aria-label={t('utilities.support')}
              >
                <Link to="/support" aria-current={isSupportPage ? 'page' : undefined}>
                  <SupportIcon className="size-[18px]" />
                </Link>
              </IconButton>
              */}
            </div>
            {/* Outside the `hidden lg:flex` group: the old panel's bell was
                visible on every breakpoint, and so is this one. */}
            <NotificationsCenter />
            <MerchantChatCenter />
            <DeliveryAccountMenu />
          </>
        }
      />
      <TopNavShell.Main data-transition="page">{children}</TopNavShell.Main>
      <TopNavShell.BottomNav className="lg:hidden">
        {/* Mode-aware, like the top nav: legacy mode's Staff/Integrations have
            no other mobile entry point. */}
        {nav.map((item) => {
          const Icon = item.icon;
          return (
            <TopNavShell.BottomNavItem
              key={item.path}
              asChild
              active={isActive(item.path)}
              aria-label={t(item.labelKey)}
            >
              <Link to={item.path}>
                <Icon className="size-6" />
              </Link>
            </TopNavShell.BottomNavItem>
          );
        })}
      </TopNavShell.BottomNav>
    </TopNavShell.Root>
  );
}
